import JournalsPagePrimaryActions from '../../journals/JournalsPagePrimaryActions';
import JournalsTable from '../../journals/JournalsTable';
import NewOrEditJournalForm from '../../new-or-edit-journal/NewOrEditJournalForm';
import SecondaryPageActions from '../../../components/secondary-page-actions/SecondaryPageActions';
import PostJournalsConfirmDialog from '../../journals/PostJournalsConfirmDialog';
import PostJournalsErrorAlert from '../../../components/post-journals-error-alert/PostJournalsErrorAlert';
import ActiveWorksheetJournal from './ActiveWorksheetJournal';
import DeleteJournalsConfirmDialog from '../../journal-overview/DeleteJournalsConfirmDialog';
import VoidJournalsConfirmDialog from '../../journal-overview/VoidJournalsConfirmDialog';
import plural from '../../../utilities/plural';
import { ButtonV2, DataGridRef } from '@cimplico/components';
import { useRef, useEffect, useState } from 'react';
import { WorksheetWithAccount } from '../../../types/worksheet';
import { JournalOverview as JournalOverviewType, JournalStatusEnum } from '../../../types/journal';
import { isNullOrUndefined } from '../../new-workpaper/utils';
import { useDeleteJournals } from '../../../queries/use-delete-journals';
import { toastNotification } from '../../../components/toast/Toast';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { WorkpaperRoleEnum } from '../../../types/workpaper';

type WorksheetJournalsProps = {
    worksheet: WorksheetWithAccount;
    onXeroError: (error?: { name: string; message: string }) => void;
};

function WorksheetJournals({ worksheet, onXeroError }: WorksheetJournalsProps): JSX.Element {
    const [searchQuery, setSearchQuery] = useState<string | undefined>('');
    const [sortQuery, setSortQuery] = useState<string | undefined>();
    const [filterQuery, setFilterQuery] = useState<string | undefined>(undefined);
    const [journalId, setJournalId] = useState<string | undefined>(undefined);
    const [selectedJournals, setSelectedJournals] = useState<JournalOverviewType[]>([]);
    const [action, setAction] = useState<string | undefined>();
    const [errorMessages, setErrorMessages] = useState<{ [key: number]: string[] } | undefined>(undefined);
    const { readOnly, role, permissions } = useWorkpaperContext();

    const tableRef = useRef<DataGridRef | null>(null);

    const deleteJournalsQuery = useDeleteJournals(worksheet.id);

    const roleCanPost =
        role === WorkpaperRoleEnum.REVIEWER ||
        role === WorkpaperRoleEnum.APPROVER ||
        (WorkpaperRoleEnum.PREPARER && permissions.preparer.canPostJournal);

    const canPostJournals = selectedJournals.filter(
        (j) =>
            j.status === JournalStatusEnum.DRAFT &&
            !isNullOrUndefined(j.debitTotal) &&
            !isNullOrUndefined(j.creditTotal) &&
            j.debitTotal === j.creditTotal
    );

    const canEditJournals = selectedJournals.filter(
        (j) => j.status !== JournalStatusEnum.VOIDED && j.status !== JournalStatusEnum.ARCHIVED
    );
    const canDeleteJournals = selectedJournals.filter((j) => j.status === JournalStatusEnum.DRAFT);
    const canVoidJournals = selectedJournals.filter((j) => j.status === JournalStatusEnum.POSTED);

    useEffect(() => {
        setAction(undefined);
        setJournalId(undefined);
    }, [worksheet.account!.id]);

    function resetTable(): void {
        setSelectedJournals([]);
        setJournalId(undefined);
        setAction(undefined);
        tableRef?.current?.clear();
    }

    function deleteJournals(): void {
        deleteJournalsQuery.mutate(
            { ids: canDeleteJournals.map((journal) => journal.id) },
            {
                onSuccess: () => {
                    resetTable();
                    toastNotification(
                        'success',
                        `${canDeleteJournals.length} draft ${plural(canDeleteJournals.length, 'journal', 'journals')} deleted!`
                    );
                },
            }
        );
    }

    return (
        <>
            <PostJournalsConfirmDialog
                journalIds={canPostJournals.map((j) => j.id)}
                isOpen={action === 'POST'}
                onClose={(): void => setAction(undefined)}
                onPost={resetTable}
                onXeroError={onXeroError}
                onPostError={setErrorMessages}
                worksheetId={worksheet.id}
            />
            {action === 'DELETE' && (
                <DeleteJournalsConfirmDialog
                    journalIds={canDeleteJournals.map((j) => j.id)}
                    onClose={(): void => setAction(undefined)}
                    onDelete={deleteJournals}
                />
            )}
            {action === 'VOID' && (
                <VoidJournalsConfirmDialog
                    worksheetId={worksheet.id}
                    journalIds={canVoidJournals.map((j) => j.id)}
                    onVoid={resetTable}
                    onClose={(): void => setAction(undefined)}
                    onXeroError={onXeroError}
                    onVoidError={setErrorMessages}
                />
            )}
            {journalId && (
                <ActiveWorksheetJournal
                    worksheet={worksheet}
                    journalId={journalId}
                    isEditing={action === 'EDIT'}
                    onXeroError={onXeroError}
                    onCancel={(): void => {
                        setJournalId(undefined);
                        setAction(undefined);
                    }}
                />
            )}
            {action === 'NEW' && (
                <NewOrEditJournalForm
                    worksheet={worksheet}
                    useSmallerStyling
                    onCreate={(id): void => {
                        setAction(undefined);
                        setJournalId(id);
                    }}
                    onCancel={(): void => setAction(undefined)}
                    onReturn={(): void => setAction(undefined)}
                    onXeroError={onXeroError}
                />
            )}
            {!journalId && action !== 'NEW' && (
                <div>
                    {selectedJournals.length > 0 && (
                        <div className="flex gap-4 items-center justify-end mb-4 h-full">
                            <SecondaryPageActions maxActionsCount={4}>
                                <div className="h-full"></div>
                                {selectedJournals.length === 1 && canEditJournals.length > 0 && (
                                    <ButtonV2
                                        type="secondary"
                                        label="Edit Journal"
                                        size="sm"
                                        onClick={(): void => {
                                            setJournalId(selectedJournals[0].id);
                                            setAction('EDIT');
                                        }}
                                        isDisabled={readOnly}
                                    />
                                )}
                                {canPostJournals.length > 0 && (
                                    <div>
                                        <ButtonV2
                                            type="secondary"
                                            label={`Post ${plural(canPostJournals.length, 'Journal', 'Journals')} (${
                                                canPostJournals.length
                                            })`}
                                            size="sm"
                                            onClick={(): void => setAction('POST')}
                                            isDisabled={readOnly || !roleCanPost}
                                            disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                        />
                                    </div>
                                )}
                                {canDeleteJournals.length > 0 && (
                                    <ButtonV2
                                        type="danger"
                                        label={`Delete ${plural(canDeleteJournals.length, 'Journal', 'Journals')} (${
                                            canDeleteJournals.length
                                        })`}
                                        size="sm"
                                        onClick={(): void => (canDeleteJournals.length > 1 ? setAction('DELETE') : deleteJournals())}
                                        isDisabled={readOnly}
                                    />
                                )}
                                {canVoidJournals.length > 0 && (
                                    <div>
                                        <ButtonV2
                                            type="danger"
                                            label={`Void ${plural(canVoidJournals.length, 'Journal', 'Journals')} (${
                                                canVoidJournals.length
                                            })`}
                                            size="sm"
                                            onClick={(): void => setAction('VOID')}
                                            isDisabled={readOnly || !roleCanPost}
                                            disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                        />
                                    </div>
                                )}
                            </SecondaryPageActions>
                        </div>
                    )}
                    {selectedJournals.length === 0 && (
                        <div className="-mt-4">
                            <JournalsPagePrimaryActions
                                onXeroError={onXeroError}
                                onSearch={setSearchQuery}
                                onSort={setSortQuery}
                                onFilter={setFilterQuery}
                                onNewJournalClick={(): void => setAction('NEW')}
                                showAccountsConfig={false}
                                disablePrimaryButton={!worksheet.account!.canPostJournal || readOnly}
                            />
                        </div>
                    )}
                    {errorMessages && <PostJournalsErrorAlert errors={errorMessages} onClose={(): void => setErrorMessages(undefined)} />}
                    <JournalsTable
                        tableRef={tableRef}
                        filter={`accounts=${worksheet.account!.id}&` + filterQuery}
                        search={searchQuery}
                        sort={sortQuery}
                        searchFilters="searchFilters=accounts"
                        onClick={setJournalId}
                        onSelect={setSelectedJournals}
                        disablePagination
                        handleNewJournal={(): void => setAction('NEW')}
                    />
                </div>
            )}
        </>
    );
}

export default WorksheetJournals;
